import {collection, doc, getDoc, setDoc, updateDoc} from 'firebase/firestore';

import {db} from '-/firebase';
import {callFunction, uploadFile} from '-/data/utils';

// The app-agnostic authoring helpers (form ↔ payload shape, step/question
// factories, label maps, rich-text utils) now live in @embertracking/ui so the
// hub can share them 1:1. Re-exported here so existing `-/data/requirements`
// imports keep working unchanged.
export {
    ensureRichText,
    isEmptyRichText,
    richTextToPlain,
    STEP_TYPES,
    makeQuestion,
    normalizeCorrectAnswer,
    STATUS_LABELS,
    ASSIGNMENT_STATUS_LABELS,
    newStepId,
    makeStep,
    prepareRequirementForForm,
    requirementFormToPayload
} from '@embertracking/ui/requirements/data';

import {requirementFormToPayload, requirementSavePayload} from '@embertracking/ui/requirements/data';

// ---------------------------------------------------------------------------
// Tenant I/O — Firestore writes (client SDK), callFunction RPCs, and file uploads
// to the tenant bucket. These are the app-specific half of the data layer; the
// hub provides its own equivalents against the hub project. Everything above is
// shared/pure.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Actor attribution (#1477)
// ---------------------------------------------------------------------------

// Who assigned / credited an assignment, as a display string — or null when
// there's nothing to attribute, so the caller can pick its own wording
// ("System" / "Auto-assigned by rule").
//
// A hub-performed action stores the person on `assignedByActor`/
// `completedByActor` and deliberately leaves the legacy uid null: a HUB uid
// resolved against THIS tenant's roster finds nothing, which is why hub-assigned
// rows used to read "System". Pre-#1477 hub rows still carry the foreign uid with
// no actor — those land on the generic "Hub", which is an improvement on
// "System" and is why nothing is backfilled.
export const actorDisplayLabel = (actor, legacyUid, usersByUid) => {
    if (actor?.type === 'HUB') return actor.name || actor.hubName || 'Hub';
    const uid = actor?.uid || legacyUid;
    const user = uid && usersByUid ? usersByUid.get(uid) : null;
    if (user) return user.fullName || user.email || uid;
    // A uid we can't resolve locally came from somewhere else — say so rather
    // than implying the system did it.
    return legacyUid ? 'Hub' : null;
};

export const assignedByLabel = (assignment, usersByUid) =>
    actorDisplayLabel(assignment?.assignedByActor, assignment?.assignedBy, usersByUid);

export const completedByLabel = (assignment, usersByUid) =>
    actorDisplayLabel(assignment?.completedByActor, assignment?.completedBy, usersByUid);

export const createRequirement = async (requirementId, data, {uid} = {}) => {
    const payload = requirementFormToPayload(data, {uid});
    await setDoc(doc(db, 'requirements', requirementId), {
        ...payload,
        status: 'DRAFT',
        version: 0,
        source: 'local',
        createdAt: new Date(),
        createdBy: uid || null
    });
};

export const saveRequirement = async (requirementId, data, options = {}) => {
    await updateDoc(doc(db, 'requirements', requirementId), requirementSavePayload(data, options));
};

export const newRequirementId = () => doc(collection(db, 'requirements')).id;

// The generic /uploadFile arrayUnions the file onto the requirement doc, so the doc
// must exist first. On a brand-new requirement the doc isn't written until first
// Save, so create a minimal DRAFT skeleton on demand (a later Save overwrites
// it with the full definition). The stray top-level `files` array uploadFile
// writes is unused — we read the returned metadata into the step instead.
const ensureRequirementDoc = async (requirementId, {uid} = {}) => {
    const ref = doc(db, 'requirements', requirementId);
    const snap = await getDoc(ref);
    if (snap.exists()) return;
    await setDoc(ref, {
        name: '',
        status: 'DRAFT',
        version: 0,
        source: 'local',
        steps: [],
        hasDraftChanges: true,
        createdAt: new Date(),
        createdBy: uid || null,
        updatedAt: new Date()
    });
};

// Upload a DOCUMENT step file. The api stores it under requirements/{requirementId}/{stepId}/{uuid}
// (grouping = stepId) and returns the file metadata we embed in the step.
export const uploadRequirementStepFile = async (requirementId, stepId, file, {uid} = {}) => {
    await ensureRequirementDoc(requirementId, {uid});
    const meta = await uploadFile(`requirements/${requirementId}`, file, stepId);
    return meta;
};

export const publishRequirement = requirementId => callFunction('publishRequirement', {id: requirementId});
export const deleteRequirement = requirementId => callFunction('deleteRequirement', {id: requirementId});
export const listRequirements = () => callFunction('listRequirements', {});
export const getRequirementDefinition = requirementId => callFunction('getRequirement', {requirementId});
export const assignRequirement = params => callFunction('assignRequirement', params);
export const cancelAssignment = assignmentId => callFunction('cancelAssignment', {assignmentId});
export const completeRequirementFor = params => callFunction('completeRequirementFor', params);
// Approve or reject a self-completion held for validation (#1017).
export const validateCompletion = params => callFunction('validateCompletion', params);

// Upload a completion-evidence file (certificate/proof) for completion or approval. Stored
// under requirements/{requirementId}/evidence/{uuid}; returns the path the api records.
//
// `sharedRequirementId` credits a HUB-shared requirement: the file still lives in
// this tenant's own bucket (departments keep custody of evidence), keyed by the
// RAW shared id — but `attachToDoc` has to be off, because there is no local
// `requirements/{sharedId}` doc for the api to arrayUnion onto and the upload
// would 404.
export const uploadRequirementEvidence = async (requirementId, file, {sharedRequirementId} = {}) => {
    return sharedRequirementId
        ? await uploadFile(`requirements/${sharedRequirementId}`, file, 'evidence', false, {attachToDoc: false})
        : await uploadFile(`requirements/${requirementId}`, file, 'evidence');
};
